import React, { useEffect, useRef } from "react";
import "./Showcase.css";

/* ------------------------------------------------------------------
   Line-art icons (stroke = currentColor). Add a case + reference it
   from a data row's `icon` field to introduce a new visual. Same
   convention as Carousel.js so the two sections read as one family.
------------------------------------------------------------------ */
const Icon = ({ name }) => {
  const common = {
    viewBox: "0 0 24 24",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (name) {
    case "chart-line":
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 14l3.5-4 3 3L21 7" />
        </svg>
      );
    case "bar-chart":
      return (
        <svg {...common}>
          <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        </svg>
      );
    case "people":
      return (
        <svg {...common}>
          <circle cx="9" cy="7" r="3" />
          <path d="M3 21v-2a5 5 0 0 1 5-5h2" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M14 21v-1a4 4 0 0 1 4-4 4 4 0 0 1 4 4v1" />
        </svg>
      );
    case "cart":
      return (
        <svg {...common}>
          <circle cx="9" cy="20" r="1.5" />
          <circle cx="18" cy="20" r="1.5" />
          <path d="M2 3h3l2.5 12h10l2-8H6" />
        </svg>
      );
    case "box":
      return (
        <svg {...common}>
          <path d="M12 3 3 8v8l9 5 9-5V8l-9-5Z" />
          <path d="M3 8l9 5 9-5M12 13v8" />
        </svg>
      );
    case "archive":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="4" rx="1" />
          <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M9 12h6" />
        </svg>
      );
    case "wallet":
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18M16 14h2" />
        </svg>
      );
    case "spine":
      return (
        <svg {...common}>
          <path d="M12 3v18M7 7l5 4 5-4M7 17l5-4 5 4" />
        </svg>
      );
    case "link":
      return (
        <svg {...common}>
          <path d="M9 13a4 4 0 0 0 5.66 0l2.5-2.5a4 4 0 0 0-5.66-5.66l-1 1" />
          <path d="M15 11a4 4 0 0 0-5.66 0l-2.5 2.5a4 4 0 0 0 5.66 5.66l1-1" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common}>
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      );
    case "globe":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "activity":
      return (
        <svg {...common}>
          <path d="M3 12h4l3 8 4-16 3 8h4" />
        </svg>
      );
    case "spark":
      return (
        <svg {...common}>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
        </svg>
      );
    default:
      return null;
  }
};

/* Seven modules orbiting the core. Angles are in degrees (0 = top,
   clockwise). r is the orbit radius as a % of the panel half-size. */
const ORBIT = [
  { icon: "chart-line", angle: 0 },
  { icon: "people", angle: 51 },
  { icon: "cart", angle: 103 },
  { icon: "archive", angle: 154 },
  { icon: "box", angle: 206 },
  { icon: "wallet", angle: 257 },
  { icon: "bar-chart", angle: 309 },
];

const polar = (angleDeg, r) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: 50 + Math.cos(rad) * r, y: 50 + Math.sin(rad) * r };
};

const CORE_BULLETS = [
  {
    icon: "spine",
    title: "One transactional spine",
    text: "Sales, finance, HCM, inventory and procurement all write to the same ledger of record.",
  },
  {
    icon: "link",
    title: "Referenced, never copied",
    text: "A customer, an item, an employee exists once — and every module points to that single record.",
  },
  {
    icon: "shield",
    title: "Governed end to end",
    text: "Permissions, approvals and audit live at the core, so control doesn't fragment per app.",
  },
];

const DASH_POINTS = [
  {
    n: "01",
    title: "Live, not loaded",
    text: "Figures stream from the core — no overnight batch, no stale export.",
  },
  {
    n: "02",
    title: "Drill to the transaction",
    text: "Click any number to land on the exact entries behind it.",
  },
  {
    n: "03",
    title: "One view across the business",
    text: "Finance, ops and people on a single pane — same data, same instant.",
  },
];

const BI_CARDS = [
  {
    icon: "activity",
    title: "Trace every metric",
    text: "Margin dipped? Follow it from the dashboard tile down to the exact orders and costs that moved it.",
  },
  {
    icon: "spark",
    title: "Decide on what's true",
    text: "No reconciling three reports first. One dataset means one answer the whole leadership team trusts.",
  },
  {
    icon: "clock",
    title: "Move while it matters",
    text: "Insight arrives the moment the transaction does — so the decision happens this week, not next quarter.",
  },
];

const TRUST_CARDS = [
  {
    icon: "shield",
    title: "Access & audit",
    text: "Role-based permissions and a complete, immutable audit trail on every record across every module.",
  },
  {
    icon: "lock",
    title: "Encryption",
    text: "Data encrypted in transit and at rest, with isolated environments per organization.",
  },
  {
    icon: "globe",
    title: "Philippine data residency",
    text: "Your business of record stays in-country, aligned to local data-privacy expectations.",
  },
  {
    icon: "clock",
    title: "Reliability",
    text: "A 99.9% uptime target with transparent status and a clear path for incident communication.",
  },
];

const NAV = ["Financial", "Human", "Sales", "Procurement", "Inventory", "Payroll"];
const KPIS = [
  { label: "Revenue · MTD", value: "₱ 24.8M", delta: "+12.4%", up: true },
  { label: "Gross margin", value: "38.6%", delta: "+1.8 pts", up: true },
  { label: "Net cash", value: "₱ 9.1M", delta: "+₱0.7M", up: true },
  { label: "Open orders", value: "1,284", delta: "−4.1%", up: false },
];

const Showcase = () => {
  const rootRef = useRef(null);

  /* Scroll reveal — fade in on enter, fade out on leave. Mirrors the
     IntersectionObserver pattern from Carousel.js so both sections feel
     identical. Honors reduced-motion and missing IO support. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".nz-reveal");

    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-in", entry.isIntersecting);
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div ref={rootRef} className="nz-show">
      {/* ============================================================
          1 · Noser Core — the core is the product
          ============================================================ */}
      <section className="nz-show__sec nz-show__core">
        <div className="nz-show__inner nz-show__split">
          <div className="nz-show__col">
            <span className="nz-show__eyebrow nz-reveal">Noser Core</span>
            <h2 className="nz-show__title nz-reveal" style={{ transitionDelay: "70ms" }}>
              The core is the product. The modules are how you use it.
            </h2>
            <p className="nz-show__lead nz-reveal" style={{ transitionDelay: "140ms" }}>
              Most "suites" are separate apps with connectors bolted on after the
              fact. Noser is the opposite: one core that the modules grew out of.
              That inversion is why your data stays consistent without a single
              integration to maintain.
            </p>
            <ul className="nz-show__bullets">
              {CORE_BULLETS.map((b, i) => (
                <li
                  key={b.title}
                  className="nz-show__bullet nz-reveal"
                  style={{ transitionDelay: `${210 + i * 70}ms` }}
                >
                  <span className="nz-show__chip">
                    <Icon name={b.icon} />
                  </span>
                  <div className="nz-show__bullet-body">
                    <h3 className="nz-show__bullet-title">{b.title}</h3>
                    <p className="nz-show__bullet-text">{b.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="nz-show__col nz-show__orbit-col">
            <div className="nz-show__orbit nz-glass nz-reveal" style={{ transitionDelay: "120ms" }}>
              {/* connector lines + dashed ring (SVG sits under the chips) */}
              <svg className="nz-show__orbit-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                <circle
                  className="nz-show__orbit-ring"
                  cx="50"
                  cy="50"
                  r="38"
                />
                {ORBIT.map((m) => {
                  const p = polar(m.angle, 38);
                  return (
                    <line
                      key={m.angle}
                      className="nz-show__orbit-line"
                      x1="50"
                      y1="50"
                      x2={p.x}
                      y2={p.y}
                    />
                  );
                })}
              </svg>

              <div className="nz-show__core-disc nz-reveal" style={{ transitionDelay: "200ms" }}>
                <span>Noser</span>
                <strong>Core</strong>
              </div>

              {ORBIT.map((m, i) => {
                const p = polar(m.angle, 38);
                return (
                  <span
                    key={m.angle}
                    className="nz-show__node nz-reveal"
                    style={{
                      left: `${p.x}%`,
                      top: `${p.y}%`,
                      transitionDelay: `${260 + i * 60}ms`,
                    }}
                  >
                    <Icon name={m.icon} />
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          2 · Unified dashboard — one screen
          ============================================================ */}
      <section className="nz-show__sec nz-show__dash">
        <div className="nz-show__inner">
          <div className="nz-show__head nz-show__head--center">
            <span className="nz-show__eyebrow nz-reveal">Unified Dashboard</span>
            <h2 className="nz-show__title nz-reveal" style={{ transitionDelay: "70ms" }}>
              What the whole business
              <br />
              looks like from one screen
            </h2>
          </div>

          <div className="nz-show__split nz-show__split--dash">
            {/* browser mockup */}
            <div className="nz-show__browser nz-glass nz-reveal" style={{ transitionDelay: "120ms" }}>
              <div className="nz-show__browser-bar">
                <span className="nz-show__dot" />
                <span className="nz-show__dot" />
                <span className="nz-show__dot" />
                <span className="nz-show__url">
                  <Icon name="lock" />
                  erp.noser.com.ph
                </span>
                <span className="nz-show__live">● Live</span>
              </div>

              <div className="nz-show__app">
                <nav className="nz-show__app-nav">
                  {NAV.map((item, i) => (
                    <span
                      key={item}
                      className={`nz-show__nav-item${i === 0 ? " is-active" : ""}`}
                    >
                      {item}
                    </span>
                  ))}
                </nav>

                <div className="nz-show__app-main">
                  <div className="nz-show__app-top">
                    <span className="nz-show__app-title">Group Dashboard</span>
                    <span className="nz-show__app-sub">Real-time · all entities</span>
                  </div>

                  <div className="nz-show__kpis">
                    {KPIS.map((k) => (
                      <div key={k.label} className="nz-show__kpi">
                        <span className="nz-show__kpi-label">{k.label}</span>
                        <span className="nz-show__kpi-value">{k.value}</span>
                        <span className={`nz-show__kpi-delta${k.up ? "" : " is-down"}`}>
                          {k.delta}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="nz-show__charts">
                    <div className="nz-show__chart">
                      <div className="nz-show__chart-head">
                        <span>Revenue vs. plan</span>
                        <span className="nz-show__chart-tag">FY '26</span>
                      </div>
                      <svg viewBox="0 0 240 90" preserveAspectRatio="none" className="nz-show__chart-svg">
                        <polyline
                          className="nz-show__line nz-show__line--plan"
                          points="0,70 40,62 80,64 120,52 160,46 200,40 240,30"
                        />
                        <polyline
                          className="nz-show__line nz-show__line--actual"
                          points="0,72 40,58 80,60 120,42 160,38 200,26 240,18"
                        />
                      </svg>
                    </div>

                    <div className="nz-show__chart">
                      <div className="nz-show__chart-head">
                        <span>By module</span>
                        <span className="nz-show__chart-tag">contribution</span>
                      </div>
                      <div className="nz-show__bars">
                        {[58, 82, 64, 40, 52].map((h, i) => (
                          <span key={i} className="nz-show__bar" style={{ height: `${h}%` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* numbered points */}
            <ol className="nz-show__points">
              {DASH_POINTS.map((p, i) => (
                <li
                  key={p.n}
                  className="nz-show__point nz-reveal"
                  style={{ transitionDelay: `${180 + i * 80}ms` }}
                >
                  <span className="nz-show__point-n">{p.n}</span>
                  <div>
                    <h3 className="nz-show__point-title">{p.title}</h3>
                    <p className="nz-show__point-text">{p.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============================================================
          3 · Business intelligence → better decisions
          ============================================================ */}
      <section className="nz-show__sec nz-show__bi">
        <div className="nz-show__inner">
          <div className="nz-show__head">
            <span className="nz-show__eyebrow nz-reveal">
              Business Intelligence → Better Decisions
            </span>
            <h2 className="nz-show__title nz-reveal" style={{ transitionDelay: "70ms" }}>
              The point of one platform isn't tidy data.
              <br />
              It's the decision at the end of it.
            </h2>
          </div>

          <div className="nz-show__grid nz-show__grid--3">
            {BI_CARDS.map((c, i) => (
              <article
                key={c.title}
                className="nz-show__card nz-glass nz-card nz-reveal"
                style={{ transitionDelay: `${120 + i * 70}ms` }}
              >
                <span className="nz-show__chip">
                  <Icon name={c.icon} />
                </span>
                <h3 className="nz-show__card-title">{c.title}</h3>
                <p className="nz-show__card-text">{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          4 · Enterprise readiness
          ============================================================ */}
      <section className="nz-show__sec nz-show__trust">
        <div className="nz-show__inner">
          <div className="nz-show__head">
            <span className="nz-show__eyebrow nz-reveal">Enterprise Readiness</span>
            <h2 className="nz-show__title nz-reveal" style={{ transitionDelay: "70ms" }}>
              Built to be trusted with
              <br />
              the system of record
            </h2>
            <p className="nz-show__lead nz-reveal" style={{ transitionDelay: "140ms" }}>
              Competing with the incumbents means meeting the same bar on security
              and governance — without the implementation drag. Here's where we
              stand today, stated plainly.
            </p>
          </div>

          <div className="nz-show__grid nz-show__grid--4">
            {TRUST_CARDS.map((c, i) => (
              <article
                key={c.title}
                className="nz-show__card nz-glass nz-card nz-reveal"
                style={{ transitionDelay: `${120 + i * 70}ms` }}
              >
                <span className="nz-show__chip">
                  <Icon name={c.icon} />
                </span>
                <h3 className="nz-show__card-title">{c.title}</h3>
                <p className="nz-show__card-text">{c.text}</p>
              </article>
            ))}
          </div>

          <p className="nz-show__note nz-reveal">
            Formal certifications are on our roadmap as we scale — we'd rather show
            you the controls than claim a badge we haven't earned.
          </p>

          {/* <div className="nz-show__pill nz-reveal" style={{ transitionDelay: "80ms" }}>
            <span className="nz-show__pill-icon">
              <Icon name="spark" />
            </span>
            <span>
              One platform,
              <br />
              end to end
            </span>
          </div> */}
        </div>
      </section>
    </div>
  );
};

export default Showcase;
